import { useMemo } from 'react'
import { useBinder } from '@/hooks/binders/useBinders'
import { useBinderSettingsActions } from '@/hooks/binders/useBinderSettingsActions'
import { useBinderSlotActions } from '@/hooks/binders/useBinderSlotActions'
import type { Binder } from '@/types'

/**
 * Consolidates binder-editor data + mutations: loads the binder, derives the
 * page/placed-card lookups, and exposes handlers with `binder.id` already
 * bound in. Callers (the page) own navigation, modal/picker state, and the
 * mobile scroll-to-page ref — this hook only knows about server state.
 */
export function useBinderEditor(id: number | undefined) {
  const { data, isLoading, error } = useBinder(id)
  const binder = data?.binder

  const slotActions = useBinderSlotActions(binder)
  const settingsActions = useBinderSettingsActions(binder)

  const pagesByNumber = useMemo(() => {
    if (!binder) return new Map<number, Binder['pages'][number]>()
    return new Map(binder.pages.map((p) => [p.pageNumber, p]))
  }, [binder])

  const placedCardIds = useMemo(() => {
    if (!binder) return new Set<number>()
    return new Set(binder.pages.flatMap((p) => p.slots.flatMap((s) => (s.card ? [s.card.id] : []))))
  }, [binder])

  return { binder, isLoading, error, pagesByNumber, placedCardIds, ...slotActions, ...settingsActions }
}
